import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../contexts/CartContext';
import './ProductDetail.css';

const toCloudinaryMain = (url) => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', '/upload/w_1000/h_1000/q_auto:good/f_auto/');
};

const toCloudinaryThumb = (url) => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', '/upload/w_150/h_150/c_pad/b_rgb:f5f5f5/q_auto:good/f_auto/');
};

// İndirimli fiyat gösterimi — eski fiyat üstü çizili + yeni fiyat + yüzde fark, rozet yok
function PriceDisplay({ product }) {
  const price = parseFloat(product.price);
  const compareAt = product.compare_at_price ? parseFloat(product.compare_at_price) : null;
  if (!compareAt || compareAt <= price) {
    return <>₺{price.toFixed(2)}</>;
  }
  const percentOff = Math.round((1 - price / compareAt) * 100);
  return (
    <>
      <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.6em', marginRight: '8px' }}>
        ₺{compareAt.toFixed(2)}
      </span>
      <span style={{ color: '#e74c3c' }}>₺{price.toFixed(2)}</span>
      <span style={{ color: '#e74c3c', fontSize: '0.5em', marginLeft: '6px' }}>(-%{percentOff})</span>
    </>
  );
}

function ProductDetail() {
  const { slug } = useParams();
  const id = slug; // API hem ID hem slug kabul ediyor
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addedToCart, setAddedToCart] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const mainImgColRef = useRef(null);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [isZooming, setIsZooming] = useState(false);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x, y });
  };

  useEffect(() => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    setIsFavorite(favorites.some(fav => fav.id === product?.id));
  }, [product]);

  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    if (isFavorite) {
      const newFavorites = favorites.filter(fav => fav.id !== product.id);
      localStorage.setItem('favorites', JSON.stringify(newFavorites));
      setIsFavorite(false);
    } else {
      favorites.push({
        id: product.id,
        name: product.name,
        price: product.price,
        image: productImages[0] || null
      });
      localStorage.setItem('favorites', JSON.stringify(favorites));
      setIsFavorite(true);
    }
  };

  useEffect(() => {
    fetchProduct();
    setSelectedImageIndex(0);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProduct = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/products/${id}`);
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
      } else {
        const products = [
          {
            id: 1,
            name: 'İpek Eşarp - Mavi',
            price: 299.90,
            description: 'Saf ipek eşarp. El işçiliği ile üretilmiş, 90x90cm boyutunda premium kalite ipek eşarp. Özel hediye kutusu ile gönderilir.',
            stock: 10,
            images: [
              'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=400',
              'https://images.unsplash.com/photo-1601924994987-69e26d50dc26?w=400'
            ]
          },
          { id: 2, name: 'İpek Eşarp - Kırmızı', price: 299.90, description: 'Saf ipek eşarp', stock: 8, images: [] },
          { id: 3, name: 'İpek Şal - Siyah', price: 499.90, description: 'Premium ipek şal', stock: 5, images: [] },
        ];
        const foundProduct = products.find(p => p.id === parseInt(id));
        setProduct(foundProduct);
      }
    } catch (error) {
      console.error('Ürün yüklenemedi:', error);
      setProduct({
        id: parseInt(id),
        name: 'İpek Ürün',
        price: 299.90,
        description: 'Premium kalite ipek ürün',
        stock: 10,
        images: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 3000);
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0 && value <= product.stock) {
      setQuantity(value);
    }
  };

  const getProductImages = useCallback(() => {
    if (!product || !product.images) return [];
    if (typeof product.images === 'string') {
      try {
        const parsed = JSON.parse(product.images);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === 'object') return Object.values(parsed).filter(url => url);
        return [];
      } catch {
        return product.images.split(',').map(url => url.trim()).filter(url => url);
      }
    }
    return Array.isArray(product.images) ? product.images : [];
  }, [product]);

  // Mouse wheel üzerinde fotoğraf değiştir, sayfa kaymaya izin verme
  const handleWheel = useCallback((e) => {
    const images = getProductImages();
    if (images.length <= 1) return;
    e.preventDefault();
    if (e.deltaY > 0) {
      setSelectedImageIndex(prev => Math.min(prev + 1, images.length - 1));
    } else {
      setSelectedImageIndex(prev => Math.max(prev - 1, 0));
    }
  }, [getProductImages]);

  useEffect(() => {
    const el = mainImgColRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <h2>Yükleniyor...</h2>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>Ürün bulunamadı</h2>
        <button onClick={() => navigate('/urunler')} style={{ marginTop: '20px', padding: '10px 20px', backgroundColor: '#667eea', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
          Ürünlere Dön
        </button>
      </div>
    );
  }

  const productImages = getProductImages();
  const hasImages = productImages.length > 0;
  const productImage = productImages[0] || '';
  const productUrl = `https://ipekaksesuar.com/urun/${product.slug || product.id}`;
  const productPrice = parseFloat(product.price).toFixed(2);

  const schemaOrgProduct = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || 'Premium kalite ipek ürün.',
    image: productImages,
    url: productUrl,
    brand: { '@type': 'Brand', name: 'İpek Aksesuar' },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'TRY',
      price: productPrice,
      availability: product.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      url: productUrl,
      seller: { '@type': 'Organization', name: 'İpek Aksesuar' }
    }
  };

  return (
    <>
      <Helmet>
        <title>{product.name} | İpek Aksesuar</title>
        <meta name="description" content={`${product.name} - ${product.description ? product.description.slice(0, 155) : 'Premium kalite ipek ürün. El işçiliği ile üretilmiş, özel hediye kutusu ile gönderilir.'}`} />
        <link rel="canonical" href={productUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} | İpek Aksesuar`} />
        <meta property="og:description" content={product.description ? product.description.slice(0, 200) : 'Premium kalite ipek ürün.'} />
        <meta property="og:url" content={productUrl} />
        {productImage && <meta property="og:image" content={productImage} />}
        <meta property="og:site_name" content="İpek Aksesuar" />
        <meta property="og:locale" content="tr_TR" />
        <meta property="product:price:amount" content={productPrice} />
        <meta property="product:price:currency" content="TRY" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.name} | İpek Aksesuar`} />
        <meta name="twitter:description" content={product.description ? product.description.slice(0, 200) : 'Premium kalite ipek ürün.'} />
        {productImage && <meta name="twitter:image" content={productImage} />}
        <script type="application/ld+json">{JSON.stringify(schemaOrgProduct)}</script>
      </Helmet>

      <div className="pd-wrapper">

        {/* ── Sol: Büyük Fotoğraf ── */}
        <div
          className={`pd-main-img-col${isZooming ? ' pd-zooming' : ''}`}
          ref={mainImgColRef}
          onMouseMove={hasImages ? handleMouseMove : undefined}
          onMouseEnter={hasImages ? () => setIsZooming(true) : undefined}
          onMouseLeave={hasImages ? () => setIsZooming(false) : undefined}
          onTouchStart={hasImages && productImages.length > 1 ? (e) => {
            e.currentTarget._touchStartX = e.touches[0].clientX;
          } : undefined}
          onTouchEnd={hasImages && productImages.length > 1 ? (e) => {
            const startX = e.currentTarget._touchStartX;
            if (startX == null) return;
            const deltaX = startX - e.changedTouches[0].clientX;
            if (Math.abs(deltaX) < 20) return;
            const dir = deltaX > 0 ? 1 : -1;
            setSelectedImageIndex(prev =>
              Math.max(0, Math.min(prev + dir, productImages.length - 1))
            );
          } : undefined}
        >
          {hasImages ? (
            <img
              src={toCloudinaryMain(productImages[selectedImageIndex])}
              alt={`${product.name} - ${selectedImageIndex + 1}`}
              className="pd-main-img"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect width="400" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EResim Yüklenemedi%3C/text%3E%3C/svg%3E';
              }}
            />
          ) : (
            <div className="pd-no-img">🧣</div>
          )}
          {hasImages && isZooming && (
            <div
              className="pd-zoom-panel"
              style={{
                backgroundImage: `url(${toCloudinaryMain(productImages[selectedImageIndex])})`,
                backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`,
                backgroundSize: '500%',
                left: mainImgColRef.current ? mainImgColRef.current.getBoundingClientRect().right + 8 : 0,
              }}
            />
          )}
          {productImages.length > 1 && (
            <div className="pd-scroll-hint">↕ Kaydır: fotoğraf değiştir</div>
          )}
        </div>

        {/* ── Orta: Thumbnail Listesi ── */}
        {hasImages && productImages.length > 1 && (
          <div className="pd-thumbs-col">
            {productImages.map((img, index) => (
              <div
                key={index}
                className={`pd-thumb${selectedImageIndex === index ? ' active' : ''}`}
                onClick={() => setSelectedImageIndex(index)}
              >
                <img
                  src={toCloudinaryThumb(img)}
                  alt={`Thumbnail ${index + 1}`}
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect width="80" height="80" fill="%23f0f0f0"/%3E%3C/svg%3E';
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* ── Sağ: Ürün Bilgileri ── */}
        <div className="pd-info-col">

          {/* Geri dön */}
          <button className="pd-back-btn" onClick={() => navigate('/urunler')}>
            ← Ürünlere Geri Dön
          </button>

          {/* Ürün Adı + Favori */}
          <div className="pd-name-row">
            <h1 className="pd-name">{product.name}</h1>
            <button
              className={`pd-fav-btn${isFavorite ? ' active' : ''}`}
              onClick={toggleFavorite}
              title={isFavorite ? 'Favorilerden kaldır' : 'Favorilere ekle'}
            >
              {isFavorite ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="#e53935">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              )}
            </button>
          </div>

          {/* Fiyat */}
          <div className="pd-price"><PriceDisplay product={product} /></div>

          {/* Boyut Seçici */}
          {product.size && (
            <div style={{ marginBottom: '22px' }}>
              <p className="pd-size-label">Boyut Seçin</p>
              <div className="pd-size-btns">
                {(product.variants && product.variants.length > 1 ? product.variants : [product]).map((variant) => {
                  const isSelected = variant.id === product.id;
                  const isOutOfStock = variant.stock === 0;
                  return (
                    <button
                      key={variant.id}
                      className={`pd-size-btn${isSelected ? ' selected' : ''}`}
                      onClick={() => !isSelected && navigate(`/urun/${variant.slug || variant.id}`)}
                      disabled={isOutOfStock}
                      title={isOutOfStock ? 'Stokta yok' : variant.size}
                    >
                      {variant.size}
                      {isOutOfStock && <span style={{ fontSize: '10px', display: 'block', color: '#bbb' }}>Stokta Yok</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Adet + Sepete Ekle */}
          <div className="pd-add-row">
            {product.stock > 0 && (
              <select
                value={quantity}
                onChange={handleQuantityChange}
                className="pd-qty-select"
              >
                {[...Array(Math.min(10, product.stock))].map((_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
            )}
            <button
              className="pd-add-btn"
              onClick={handleAddToCart}
              disabled={product.stock === 0}
            >
              {product.stock === 0 ? 'Stoğu Tükendi' : 'Sepete Ekle'}
            </button>
          </div>

          {/* Sepete eklendi bildirimi */}
          {addedToCart && (
            <div className="pd-added-notice">✓ Ürün sepete eklendi!</div>
          )}

          {/* Stok */}
          <div className="pd-stock">
            {product.stock > 0 ? `Stokta ${product.stock} adet var` : 'Stokta yok'}
          </div>

          {/* Ürün Açıklaması */}
          {product.description && (
            <div className="pd-desc">
              {product.description}
            </div>
          )}

          {/* Ürün Özellikleri */}
          <div className="pd-features">
            <h3 className="pd-features-title">Ürün Özellikleri</h3>
            <ul className="pd-features-list">
              <li>✓ %100 Saf İpek</li>
              <li>✓ Süper El Dikişi</li>
              <li>✓ Özel MEBS Paketi</li>
              <li>✓ 1-3 Gün İçinde Kargo</li>
            </ul>
          </div>

          {/* Yıkama Talimatı */}
          <div className="pd-features">
            <h3 className="pd-features-title">Yıkama Talimatı</h3>
            <p className="pd-wash-intro">Ürün doğası gereği hassas bir yapıya sahiptir. Bu sebepten:</p>
            <ul className="pd-features-list">
              <li>🧺 Yalnızca kuru temizleme önerilir</li>
              <li>🌡️ Ütü orta ısıda yapılabilir</li>
              <li>🚫 Elde yıkama yapılmaz</li>
              <li>🚫 Makinada temizleme yapılmaz</li>
              <li>🚫 Sıkma yapılmaz</li>
            </ul>
          </div>

        </div>
      </div>
    </>
  );
}

export default ProductDetail;
